// kast-dev family file: refreshSession; master: pocket-play/ui/src/common/auth/refreshSession.ts
// Exchanging the stored refresh token for new access and id tokens. One
// file, copied unchanged into every kast-dev web app (pocket-play is the
// reference); change them together.
//
// Sign-in happens on login.kast-dev.net with the Cognito SDK, and a refresh
// token issued that way cannot be exchanged at the hosted UI's /oauth2/token
// endpoint, so this calls InitiateAuth with REFRESH_TOKEN_AUTH.

export interface RefreshedTokens {
  access_token?: string;
  id_token?: string;
  refresh_token?: string;
}

export interface RefreshConfig {
  /** The hosted UI domain, e.g. https://x.auth.us-east-1.amazoncognito.com; only its region is read. */
  cognitoDomain: string;
  clientId: string;
}

/**
 * ok: new tokens. rejected: Cognito refused the refresh token, so the user
 * must sign in again. transient: network, 5xx or missing config; keep the
 * session and try later.
 */
export type RefreshResult = { outcome: "ok"; tokens: RefreshedTokens } | { outcome: "rejected" | "transient" };

export const AUTH_STORAGE_KEYS = {
  accessToken: "auth_access_token",
  idToken: "auth_id_token",
  refreshToken: "auth_refresh_token",
} as const;

const inFlight = new Map<string, Promise<RefreshResult>>();

/**
 * Callers that ask while a refresh for the same token is running share its
 * request. Several failed calls at once would otherwise each refresh, and a
 * rotated refresh token would be spent by the first.
 */
export function refreshSession(config: RefreshConfig, refreshToken: string): Promise<RefreshResult> {
  const running = inFlight.get(refreshToken);
  if (running) return running;
  const request = exchange(config, refreshToken).finally(() => inFlight.delete(refreshToken));
  inFlight.set(refreshToken, request);
  return request;
}

/** Writes only the tokens that came back: a refresh returns no new refresh token. */
export function storeRefreshedTokens(tokens: RefreshedTokens): void {
  if (tokens.access_token) localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, tokens.access_token);
  if (tokens.id_token) localStorage.setItem(AUTH_STORAGE_KEYS.idToken, tokens.id_token);
  if (tokens.refresh_token) localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, tokens.refresh_token);
}

/** A stalled refresh would hold every request queued behind it; give up and treat it as transient. */
const REFRESH_TIMEOUT_MS = 8000;

function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal.timeout === "function") return AbortSignal.timeout(ms);
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

function endpoint(cognitoDomain: string): string {
  const region = cognitoDomain.match(/\.auth\.([a-z0-9-]+)\.amazoncognito\.com/)?.[1] ?? "us-east-1";
  return `https://cognito-idp.${region}.amazonaws.com/`;
}

async function exchange(config: RefreshConfig, refreshToken: string): Promise<RefreshResult> {
  if (!config.clientId) return { outcome: "transient" };
  let response: Response;
  try {
    response = await fetch(endpoint(config.cognitoDomain), {
      method: "POST",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
      },
      body: JSON.stringify({
        AuthFlow: "REFRESH_TOKEN_AUTH",
        ClientId: config.clientId,
        AuthParameters: { REFRESH_TOKEN: refreshToken },
      }),
      signal: timeoutSignal(REFRESH_TIMEOUT_MS),
    });
  } catch {
    return { outcome: "transient" };
  }
  if (!response.ok) return { outcome: response.status >= 400 && response.status < 500 ? "rejected" : "transient" };
  try {
    const result = ((await response.json()) as { AuthenticationResult?: { AccessToken?: string; IdToken?: string } })
      .AuthenticationResult;
    if (!result?.AccessToken && !result?.IdToken) return { outcome: "transient" };
    return { outcome: "ok", tokens: { access_token: result.AccessToken, id_token: result.IdToken } };
  } catch {
    return { outcome: "transient" };
  }
}
